if (!customElements.get('store-locator')) {
  customElements.define(
    'store-locator',
    class StoreLocator extends HTMLElement {
      constructor() {
        super();
        this.strings = JSON.parse(this.querySelector('[data-strings]').textContent);
        this.status = this.querySelector('[data-status]');
        this.results = this.querySelector('[data-results]');
        this.resetButton = this.querySelector('[data-reset]');
        this.zipInput = this.querySelector('[data-zip-input]');
        this.groups = Array.from(this.querySelectorAll('[data-group]'));
        this.filterButtons = Array.from(this.querySelectorAll('[data-filter]'));
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.stores = Array.from(this.querySelectorAll('[data-store]')).map((element) => ({
          element,
          list: element.parentElement,
          state: element.dataset.state,
          name: element.dataset.name,
          locality: element.dataset.locality,
          lat: parseFloat(element.dataset.lat),
          lng: parseFloat(element.dataset.lng),
          distance: element.querySelector('[data-distance]'),
          directions: element.querySelector('a[href*="google.com/maps"]'),
          showOnMap: element.querySelector('[data-show-on-map]'),
        }));

        this.querySelector('[data-search-form]').addEventListener('submit', this.onSearch.bind(this));
        this.filterButtons.forEach((button) =>
          button.addEventListener('click', () => this.filterByState(button.dataset.filter))
        );
        this.resetButton.addEventListener('click', () => this.filterByState('all'));
        this.stores.forEach((store) => {
          if (store.showOnMap) store.showOnMap.addEventListener('click', () => this.showOnMap(store));
        });

        const locateButton = this.querySelector('[data-locate]');
        if ('geolocation' in navigator) {
          locateButton.hidden = false;
          locateButton.addEventListener('click', this.onLocate.bind(this));
        }
      }

      connectedCallback() {
        const container = this.querySelector('[data-map]');
        if (!container) return;

        const start = () =>
          this.loadLeaflet()
            .then(() => this.initMap(container))
            .catch(() => {
              container.closest('.store-locator__map-wrapper').hidden = true;
            });

        if (!('IntersectionObserver' in window)) return start();
        const observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            observer.disconnect();
            start();
          },
          { rootMargin: '200px' }
        );
        observer.observe(container);
      }

      loadLeaflet() {
        if (window.L) return Promise.resolve();
        if (!window.storeLocatorLeaflet) {
          window.storeLocatorLeaflet = new Promise((resolve, reject) => {
            const stylesheet = document.createElement('link');
            stylesheet.rel = 'stylesheet';
            stylesheet.href = this.dataset.leafletCss;
            document.head.appendChild(stylesheet);

            const script = document.createElement('script');
            script.src = this.dataset.leafletJs;
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }
        return window.storeLocatorLeaflet;
      }

      initMap(container) {
        this.map = L.map(container, { scrollWheelZoom: false, zoomSnap: 0.5 });
        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 18,
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        }).addTo(this.map);

        // Scroll-zoom only after the visitor presses anywhere on the map (pins, popups and drags included),
        // until the pointer leaves it, so the page still scrolls past the map
        container.addEventListener('pointerdown', () => this.map.scrollWheelZoom.enable(), true);
        container.addEventListener('mouseleave', () => this.map.scrollWheelZoom.disable());

        // The map gets shorter while the sticky header is showing; redraw tiles for the new size
        new ResizeObserver(() => this.map.invalidateSize({ pan: false })).observe(container);

        const icon = L.divIcon({
          className: 'store-locator__pin',
          html: '<svg viewBox="0 0 32 40" width="32" height="40" aria-hidden="true" focusable="false"><path d="M16 39s13-13.2 13-23a13 13 0 0 0-26 0c0 9.8 13 23 13 23z" fill="currentColor" stroke="#fff" stroke-width="2"/><path d="M16 16.5a1.5 1.5 0 1 1 1.5-1.5 3 3 0 0 1-3 3 4.5 4.5 0 0 1-4.5-4.5 6 6 0 0 1 6-6 7.5 7.5 0 0 1 7.5 7.5" fill="none" stroke="#fff" stroke-width="1.8" stroke-linecap="round"/></svg>',
          iconSize: [32, 40],
          iconAnchor: [16, 40],
          popupAnchor: [0, -36],
        });

        this.stores.forEach((store) => {
          if (!this.hasLocation(store)) return;
          store.marker = L.marker([store.lat, store.lng], { icon, title: store.name, keyboard: false }).addTo(this.map);
          store.marker.bindPopup(() => this.popupContent(store));
          store.marker.on('click', () => this.highlight(store));
          if (store.showOnMap) store.showOnMap.hidden = false;
        });

        this.fitTo(this.visibleStores());
      }

      popupContent(store) {
        const content = document.createElement('div');
        content.className = 'store-locator__popup';

        const name = document.createElement('p');
        name.className = 'store-locator__popup-name';
        name.textContent = store.name;
        const locality = document.createElement('p');
        locality.textContent = store.locality;
        content.append(name, locality);

        if (store.directions) {
          const link = document.createElement('a');
          link.href = store.directions.href;
          link.target = '_blank';
          link.rel = 'noopener';
          link.textContent = this.strings.directions;
          content.append(link);
        }
        return content;
      }

      hasLocation(store) {
        return Number.isFinite(store.lat) && Number.isFinite(store.lng);
      }

      visibleStores() {
        return this.stores.filter((store) => !store.element.closest('[hidden]'));
      }

      fitTo(stores, origin) {
        if (!this.map) return;
        const points = stores.filter((store) => this.hasLocation(store)).map((store) => [store.lat, store.lng]);
        if (origin) points.push(origin);
        if (!points.length) return;
        if (points.length === 1) {
          this.map.setView(points[0], 11, { animate: !this.reducedMotion });
        } else {
          this.map.fitBounds(points, { padding: [40, 40], maxZoom: 11, animate: !this.reducedMotion });
        }
      }

      highlight(store) {
        this.stores.forEach((item) => {
          item.element.classList.toggle('is-active', item === store);
          if (item.marker) item.marker.getElement()?.classList.toggle('is-active', item === store);
        });
      }

      showOnMap(store) {
        if (!this.map || !store.marker) return;
        this.highlight(store);
        this.map.setView([store.lat, store.lng], 13, { animate: !this.reducedMotion });
        store.marker.openPopup();

        const rect = this.map.getContainer().getBoundingClientRect();
        if (rect.top < 0 || rect.bottom > window.innerHeight) {
          this.map.getContainer().scrollIntoView({ block: 'center', behavior: this.reducedMotion ? 'auto' : 'smooth' });
        }
      }

      setStatus(message, isError = false) {
        this.status.textContent = message;
        this.status.classList.toggle('store-locator__status--error', isError);
      }

      setPressed(filter) {
        this.filterButtons.forEach((button) =>
          button.setAttribute('aria-pressed', button.dataset.filter === filter ? 'true' : 'false')
        );
      }

      clearDistances() {
        if (this.originMarker) {
          this.originMarker.remove();
          this.originMarker = null;
        }
        this.stores.forEach((store) => {
          store.distance.hidden = true;
          store.distance.textContent = '';
          store.element.classList.remove('is-closest');
          store.list.appendChild(store.element);
        });
        this.results.hidden = true;
      }

      filterByState(filter) {
        if (!this.results.hidden) this.zipInput.value = '';
        this.clearDistances();
        this.setPressed(filter);
        this.groups.forEach((group) => {
          group.hidden = filter !== 'all' && group.dataset.group !== filter;
        });
        this.resetButton.hidden = filter === 'all';

        const visible = this.visibleStores();
        if (filter === 'all') {
          this.setStatus(this.strings.all.replace('[count]', visible.length));
        } else {
          const button = this.filterButtons.find((item) => item.dataset.filter === filter);
          const stateName = button.firstChild.textContent.trim();
          this.setStatus(this.strings.inState.replace('[count]', visible.length).replace('[state]', stateName));
        }
        this.highlight(null);
        this.fitTo(visible);
      }

      sortByDistance(origin, message) {
        this.clearDistances();
        this.setPressed(null);
        this.groups.forEach((group) => (group.hidden = true));

        const sorted = this.stores
          .map((store) => ({ store, miles: this.hasLocation(store) ? this.miles(origin, store) : Infinity }))
          .sort((a, b) => a.miles - b.miles);

        sorted.forEach(({ store, miles }, index) => {
          if (Number.isFinite(miles)) {
            store.distance.textContent = this.strings.distance.replace('[miles]', Math.max(1, Math.round(miles)));
            store.distance.hidden = false;
          }
          store.element.classList.toggle('is-closest', index === 0 && Number.isFinite(miles));
          this.results.appendChild(store.element);
        });

        this.results.hidden = false;
        this.resetButton.hidden = false;
        this.setStatus(message);
        this.highlight(sorted[0].store);

        if (this.map) {
          this.originMarker = L.circleMarker(origin, {
            className: 'store-locator__origin',
            radius: 8,
            interactive: false,
          }).addTo(this.map);
        }
        this.fitTo(
          sorted.slice(0, 3).map((item) => item.store),
          origin
        );
      }

      miles(origin, store) {
        const toRadians = (degrees) => (degrees * Math.PI) / 180;
        const dLat = toRadians(store.lat - origin[0]);
        const dLng = toRadians(store.lng - origin[1]);
        const a =
          Math.sin(dLat / 2) ** 2 +
          Math.cos(toRadians(origin[0])) * Math.cos(toRadians(store.lat)) * Math.sin(dLng / 2) ** 2;
        return 3958.8 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      }

      loadZips() {
        if (!this.zipLookup) {
          this.zipLookup = fetch(this.dataset.zipUrl).then((response) => {
            if (!response.ok) throw new Error(response.status);
            return response.json();
          });
          this.zipLookup.catch(() => (this.zipLookup = null));
        }
        return this.zipLookup;
      }

      onSearch(event) {
        event.preventDefault();
        const zip = this.zipInput.value.trim();
        if (!/^\d{5}$/.test(zip)) {
          this.setStatus(this.strings.zipInvalid, true);
          this.zipInput.focus();
          return;
        }

        this.loadZips()
          .then((zips) => {
            const origin = zips[zip.slice(0, 3)];
            if (!origin) {
              this.setStatus(this.strings.zipNotFound, true);
              return;
            }
            this.sortByDistance(origin, this.strings.nearZip.replace('[zip]', zip));
          })
          .catch(() => this.setStatus(this.strings.zipNotFound, true));
      }

      onLocate(event) {
        const button = event.currentTarget;
        button.disabled = true;
        this.setStatus(this.strings.locating);

        navigator.geolocation.getCurrentPosition(
          (position) => {
            button.disabled = false;
            this.zipInput.value = '';
            this.sortByDistance([position.coords.latitude, position.coords.longitude], this.strings.nearYou);
          },
          () => {
            button.disabled = false;
            this.setStatus(this.strings.locationError, true);
          },
          { timeout: 10000, maximumAge: 600000 }
        );
      }
    }
  );
}
